import { AnyAction } from 'redux';
import { handleActions, combineActions } from 'redux-actions';

import {
  createNotificationAction,
} from '@reducers/notification/NotificationActionConfig';
import {
  updateDentistProfileAction,
  updateClinicProfileAction,
  updateLabProfileAction,
  inviteDentistAction,
  cancelDentistInviteAction,
} from '@reducers/profile/ProfileActionConfig';

import {
  loginAction,
  logoutAction,
  clearUserTokenAction,
  verifyEmailAction,
  loginWithGoogleAction,
  loginWithFacebookAction,
  loginWithTwitterAction,
  forgotPassSendVerificationCodeAction,
  forgotPassVerifyCodeAction,
  sendVerificationCodeAction,
  verifyCodeAction,
  changePasswordAction,
  getUserDetailsAction,
  loginWithMicrosoftAction,
  updateOnboardingStatusAction,
  registrationTypeAction,
  updateEmailOrMobileAction,
  setVerificationAccessTokenAction,
} from './AuthActionConfig';

// reducers
const defaultState = {
  accessToken: null,
  user: {
    id: '',
    is_binded: false,
    is_onboarded: false,
    email: '',
    mobile_number: '',
    image: '',
    first_name: '',
    last_name: '',
    name: '',
    type: '',
    is_email_verified: false,
    is_mobile_verified: false,
    full_name: '',
    affiliated_dentists: [],
    subscription: null,
    invited_dentists_count: 0,
    notifications: {},
  },
  errors: {},
  codeValidityInSeconds: 120,
  registration: {
    emailOrMobile: '',
    timestamp: '',
    verification_code: '',
  },
  forgotPassword: {
    email: '',
    timestamp: '',
    account_reset_id: '',
    verification_code: '',
  },
  referrer: {},
  registrationType: '',
  isFromLogoutButton: false,
};

type State = typeof defaultState;

const reducer = handleActions<State, AnyAction>({
  [`${combineActions(loginAction, loginWithGoogleAction,
    loginWithFacebookAction, loginWithTwitterAction, loginWithMicrosoftAction)}`](state, { payload: rawPayload, params }: AnyAction) {
    // Phase 3 (backend): auth/login and auth/login/external now answer
    // {data: {user, ...}} instead of {user, ...} directly.
    const payload = rawPayload?.data?.user ? rawPayload.data : rawPayload;
    const affiliatedDentists = payload?.user?.affiliated_dentists
      && payload?.user?.affiliated_dentists.map((item: any) => item.id);
    const updatedUser = {
      ...payload.user,
      affiliated_dentists: affiliatedDentists ?? [],
    };
    return {
      ...state,
      // accessToken: payload.access_token,
      user: updatedUser,
      account: {
        ...updatedUser,
        type: 'user',
      },
      referrer: params.referrer_id ? {} : state.referrer,
    };
  },
  [`${logoutAction}`](state: State) {
    return {
      ...defaultState,
      referrer: state.referrer,
      isFromLogoutButton: true,
    };
  },
  [`${getUserDetailsAction}`](state: State, { payload: rawPayload }: AnyAction) {
    // Merge over the existing user object instead of replacing it. The
    // backend can legitimately return a partial payload (a polled refresh
    // that omits a field, an outdated server response under load, etc.).
    // A naive spread of `payload` alone would wipe locally-known fields
    // like `subscription` if they weren't included in this response.
    // Phase 3 (backend): auth/me now answers {data: <user>} instead of the user directly.
    const safePayload = rawPayload?.data ?? rawPayload ?? {};
    const existingUser = state.user ?? {};
    const affiliatedDentists = safePayload?.affiliated_dentists
      && safePayload.affiliated_dentists.map((item: any) => item.id);
    const updatedUser = {
      ...existingUser,
      ...safePayload,
      affiliated_dentists: affiliatedDentists
        ?? existingUser?.affiliated_dentists
        ?? [],
    };
    return {
      ...state,
      user: updatedUser,
      isFromLogoutButton: false,
    };
  },
  [`${verifyEmailAction}`](state: State, { payload }: AnyAction) {
    return {
      ...state,
      accessToken: payload.accessToken,
      user: payload.user,
      referrer: {},
    };
  },
  [`${combineActions(updateDentistProfileAction, updateClinicProfileAction, updateLabProfileAction)}`](state, { payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): now answers {data: <profile>} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    let newUser: any = { ...state.user };

    newUser = {
      ...newUser,
      name: payload?.name,
      first_name: payload?.first_name,
      last_name: payload?.last_name,
      mobile_number: payload?.mobile_number,
      email: payload?.email,
      image: payload?.image,
      full_name: payload?.full_name,
    };

    return {
      ...state,
      user: newUser,
    };
  },
  [`${inviteDentistAction}`](state, { payload }: AnyAction) {
    if (!payload) return state;

    return {
      ...state,
      user: {
        ...state.user,
        invited_dentists_count: state.user.invited_dentists_count + 1,
      },
    };
  },
  [`${cancelDentistInviteAction}`](state, { payload }: AnyAction) {
    if (!payload || state.user.invited_dentists_count <= 0) return state;

    return {
      ...state,
      user: {
        ...state.user,
        invited_dentists_count: state.user.invited_dentists_count - 1,
      },
    };
  },
  [`${clearUserTokenAction}`]() {
    return defaultState;
  },
  [`${combineActions(sendVerificationCodeAction, forgotPassSendVerificationCodeAction)}`](state, { payload: rawPayload, params }: AnyAction) {
    // Phase 3 (backend): now answers {data: {codeValidityInSeconds}} (or
    // {data: {resendRemainingInSeconds}}) instead of the object directly.
    const payload = rawPayload?.data ?? rawPayload;
    if (params.type === 'registration') {
      return {
        ...state,
        codeValidityInSeconds: payload.codeValidityInSeconds,
        registration: {
          emailOrMobile: params?.payload_type === 'mobile' ? `+1${params.email_or_mobile}` : params.email_or_mobile,
          timestamp: new Date(),
        } as any,
      };
    }
    return {
      ...state,
      codeValidityInSeconds: payload.codeValidityInSeconds,
      forgotPassword: {
        email: params.email,
        timestamp: new Date(),
      } as any,
    };
  },
  [`${combineActions(verifyCodeAction, forgotPassVerifyCodeAction)}`](state, { payload: rawPayload, params }: AnyAction) {
    // Phase 3 (backend): now answers {data: <verification code row id>}
    // instead of the bare id directly.
    const payload = rawPayload?.data ?? rawPayload;
    if (params.type === 'registration') {
      return {
        ...state,
        codeValidityInSeconds: 0,
        registration: {
          ...state.registration,
          verification_code: params.verification_code,
          timestamp: null,
        },
        user: {
          ...state.user,
          [params.payload_type === 'email' ? 'is_email_verified' : 'is_mobile_verified']: true,
        },
      };
    }
    return {
      ...state,
      codeValidityInSeconds: 0,
      forgotPassword: {
        ...state.forgotPassword,
        account_reset_id: payload,
        verification_code: params.verification_code,
        timestamp: null,
      },
    };
  },
  [`${updateEmailOrMobileAction}`](state, { params }: AnyAction) {
    const rawValue = params?.email_or_mobile ?? '';
    let parsedMobile = rawValue;

    if (params?.payload_type === 'mobile' && typeof rawValue === 'string' && !rawValue.startsWith('+1')) {
      parsedMobile = `+1${rawValue}`;
    }

    return {
      ...state,
      user: {
        ...state.user,
        [params?.payload_type === 'email' ? 'email' : 'mobile_number']: params?.payload_type === 'mobile' ? parsedMobile : rawValue,
      },
    };
  },
  [`${changePasswordAction}`](state: State) {
    return {
      ...state,
      forgotPassword: {
        email: '',
      },
    };
  },
  [`${updateOnboardingStatusAction}`](state, { payload }: AnyAction) {
    return {
      ...state,
      user: {
        ...state.user,
        is_onboarded: payload,
      },
    };
  },
  [`${registrationTypeAction}`](state: State, { payload }: AnyAction) {
    return {
      ...state,
      registrationType: payload,
    };
  },
  [`${setVerificationAccessTokenAction}`](state: State, { payload }: AnyAction) {
    return {
      ...state,
      accessToken: payload,
    };
  },
  [`${createNotificationAction}`](state: State, { payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): notification/{id}/update now answers {data: {notifications}}
    // instead of {notifications} directly; preserve the same (odd, pre-existing)
    // double-nested shape this handler already relied on.
    const payload = rawPayload?.data ?? rawPayload;
    return {
      ...state,
      user: {
        ...state.user,
        notifications: {
          ...state.user.notifications,
          value: payload,
        },
      },
    };
  },
}, defaultState);

export default reducer;
